import { getOneMonthRangeFromToday } from 'components/BKJDatePicker/src/BKJDatePicker.utils';
import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { DateTime } from 'luxon';
import {
  ShiftsListPageInputValues,
  ShiftsListPagePayload,
  ShiftsListPagePayloadTransformer,
} from 'pages/ShiftsListPage/ShiftsListPage.types';
import { shiftsListInitialSortFields } from 'pages/ShiftsPage/ShiftsPage.constants';
import { QUERY_shiftsListPage } from 'queries';
import { SessionService } from 'services';

const payloadTransformer: ShiftsListPagePayloadTransformer = (variables) => {
  const startFrom = DateTime.fromISO(variables.filterDate[0]).toFormat('yyyy-MM-dd');
  const startTo = DateTime.fromISO(variables.filterDate[1]).toFormat('yyyy-MM-dd');
  let payload: ShiftsListPagePayload = {
    sort: [],
    currentPage: variables.currentPage,
    first: variables.first,
    last: variables.last,
    after: variables.after,
    before: variables.before,
    filter: {
      forJanes: {
        byFullName: variables.byFullName,
      },
      forOrders: {
        withStatus: variables.shiftStatus,
        withPositions: variables.position_id,
        withCommunity: undefined as number | undefined,
        withJaneType: [...variables.internalJaneType],
        withStaff: {
          types: [...variables.externalJaneType],
          facility: {
            id: Number(variables.location.id),
            type: SessionService.assertUserType('B2B') ? 'Community' : 'AgencyCommunity',
          },
        },
        withTimeFrame: variables.timeFrameType,
        startBetweenTimeRange: {
          from: `${DateTime.fromFormat(startFrom, 'yyyy-MM-dd', {
            zone: variables.timeZone,
          }).toISO()}`,
          to: `${DateTime.fromFormat(startTo, 'yyyy-MM-dd', { zone: variables.timeZone })
            .set({ hour: 23, minute: 59 })
            .toISO()}`,
        },
        withActionsRequired: variables.actionsRequiredType,
      },
      forAgencyCommunities: {
        withId: undefined as number | undefined,
      },
    },
  };
  const sortFields = variables.sortField
    ? [JSON.parse(variables.sortField)]
    : shiftsListInitialSortFields;
  if (sortFields.some((field) => field.direction !== 'default')) {
    payload.sort = sortFields.map((field) => {
      return {
        field: field.name,
        direction: field.direction,
        nulls: 'last',
      };
    });
  }

  if (!variables.viewAllFacilities) {
    if (SessionService.assertUserType('B2B'))
      payload.filter.forOrders['withCommunity'] = Number(variables.location.value);
    if (
      SessionService.assertUserType('Agency') &&
      !variables.isUserAgencyAndAgencyLocationFilterForcedDisabled
    )
      payload.filter.forAgencyCommunities['withId'] = Number(variables.location.value);
  }
  return payload;
};

export const shiftsListPageConfig = ({
  location,
  isUserAgencyAndAgencyLocationFilterForcedDisabled,
}: {
  location: ILocationFilterProps['value'];
  isUserAgencyAndAgencyLocationFilterForcedDisabled: boolean;
}): ISSFBehaviorsOptions => {
  return {
    key: 'SHIFTS_LIST_PAGE',
    query: QUERY_shiftsListPage,
    pageSize: 20,
    payloadTransformer: (variables: ShiftsListPageInputValues) =>
      payloadTransformer({
        ...variables,
        isUserAgencyAndAgencyLocationFilterForcedDisabled,
        timeZone: (location as { timeZone?: string })?.timeZone || undefined,
      }),
    schema: {
      shiftStatus: {
        initialValue: [],
        isSessionCached: true,
      },
      employmentStatus: {
        initialValue: [],
        isSessionCached: true,
      },
      position_id: {
        initialValue: [],
        isSessionCached: true,
      },
      filterDate: {
        initialValue: getOneMonthRangeFromToday(),
        isSessionCached: true,
      },
      location: {
        initialValue: location,
        isGlobalCached: true,
        isPreservedOnReset: true,
        resetFieldsOnChange: [
          {
            field: 'position_id',
            userTypes: ['Agency'],
          },
        ],
      },
      internalJaneType: {
        initialValue: [],
        isSessionCached: true,
      },
      timeFrameType: {
        initialValue: [],
        isSessionCached: true,
      },
      externalJaneType: {
        initialValue: [],
        isSessionCached: true,
      },
      byFullName: {
        initialValue: '',
        isSessionCached: true,
      },
      actionsRequiredType: {
        initialValue: [],
        isSessionCached: true,
      },
      viewAllFacilities: {
        initialValue: false,
        isGlobalCached: true,
        isSessionCached: true,
        isPreservedOnReset: true,
      },
    },
  };
};
